import React, { useState, useEffect } from 'react';
import { AlertBand } from '@/components/AlertBand';
import { ArrowLeft, Calendar, MapPin, Clock, Ticket, ChevronRight, Loader2, Music, PartyPopper, Theater, Trophy, UtensilsCrossed, Church, CalendarDays } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/lib/api';
import SEO from '@/components/SEO';
import { MarkdownText } from '@/components/MarkdownText';
import { PlaceLinks, buildEventLinks } from '@/components/PlaceLinks';
import { EventsList } from '@/components/EventsList';
import { ShareButton } from '@/components/ShareButton';

const CATEGORY_ICONS = {
  concerts: Music,
  festivals: PartyPopper,
  cultural: Theater,
  sports: Trophy,
  gastronomy: UtensilsCrossed,
  traditional: Church,
  general: CalendarDays,
};

const CATEGORY_COLORS = {
  concerts: 'bg-purple-500',
  festivals: 'bg-pink-500',
  cultural: 'bg-blue-500',
  sports: 'bg-green-500',
  gastronomy: 'bg-orange-500',
  traditional: 'bg-amber-600',
  general: 'bg-gray-500',
};

const EventsPage = () => {
  const { language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    Promise.all([
      api.getEvents({ upcoming_only: true }),
      api.getEventCategories()
    ]).then(([eventsRes, catsRes]) => {
      setEvents(eventsRes.data);
      setCategories(catsRes.data);
      setLoading(false);
      const wanted = new URLSearchParams(window.location.search).get('event');
      if (wanted) {
        const found = eventsRes.data.find((e) => e.id === wanted);
        if (found) setSelectedEvent(found);
        else api.getEvent(wanted).then((r) => setSelectedEvent(r.data)).catch(() => {});
      }
    }).catch(() => setLoading(false));
  }, []);

  const getField = (obj, field) => obj[`${field}_${language}`] || obj[`${field}_es`] || '';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : language === 'en' ? 'en-US' : 'es-ES', options);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.slice(0, 5);
  };

  // Event detail view
  if (selectedEvent) {
    const Icon = CATEGORY_ICONS[selectedEvent.category] || CalendarDays;
    const colorClass = CATEGORY_COLORS[selectedEvent.category] || 'bg-gray-500';
    
    return (
      <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="event-detail">
        <div className="relative">
          {selectedEvent.image ? (
            <img loading="lazy" src={selectedEvent.image} alt={getField(selectedEvent, 'title')} className="w-full h-56 object-cover" />
          ) : (
            <div className={`w-full h-56 ${colorClass} flex items-center justify-center`}>
              <Icon size={64} className="text-white/50" />
            </div>
          )}
          <button 
            onClick={() => setSelectedEvent(null)}
            className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow"
            data-testid="event-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <ShareButton kind="e" itemId={selectedEvent.id} variant="round" className="absolute top-4 left-16" testId="share-event-btn" />
          <div className={`absolute top-4 right-4 ${colorClass} text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1`}>
            <Icon size={14} />
            {categories.find(c => c.slug === selectedEvent.category)?.[`name_${language}`] || selectedEvent.category}
          </div>
        </div>

        <div className="px-5 py-6">
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-4">{getField(selectedEvent, 'title')}</h1>
          <AlertBand type="movilidad" eventId={selectedEvent.id} className="mb-5" />
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-gray-600">
              <div className="w-10 h-10 rounded-full bg-[#38B2AC]/10 flex items-center justify-center">
                <Calendar size={18} className="text-[#38B2AC]" />
              </div>
              <div>
                <p className="font-medium text-[#1D1D1F]">{formatDate(selectedEvent.event_date)}</p>
                {selectedEvent.event_time && (
                  <p className="text-sm text-gray-500">{formatTime(selectedEvent.event_time)}</p>
                )}
              </div>
            </div>

            {selectedEvent.venue_name && (
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <MapPin size={18} className="text-blue-500" />
                </div>
                <div data-testid="event-venue">
                  <p className="font-medium text-[#1D1D1F]">{selectedEvent.venue_name}</p>
                  {selectedEvent.venue_address && (
                    <p className="text-sm text-gray-500">{selectedEvent.venue_address}</p>
                  )}
                </div>
              </div>
            )}

            {selectedEvent.price_info && (
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                  <Ticket size={18} className="text-amber-500" />
                </div>
                <div>
                  <p className="font-medium text-[#1D1D1F]">{selectedEvent.price_info}</p>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <PlaceLinks links={buildEventLinks(selectedEvent)} testId="event-links" />
          </div>

          {getField(selectedEvent, 'description') && (
            <div className="mb-6">
              <h2 className="font-semibold text-[#1D1D1F] mb-2">
                {language === 'es' ? 'Descripcion' : language === 'de' ? 'Beschreibung' : 'Description'}
              </h2>
              <MarkdownText text={getField(selectedEvent, 'description')} className="text-gray-600 leading-relaxed" />
            </div>
          )}

          {selectedEvent.source && selectedEvent.source !== 'manual' && (
            <p className="text-center text-xs text-gray-400 mt-4" data-testid="event-source">
              {language === 'es' ? 'Fuente' : 'Source'}: {
                {
                  ticketmaster: 'Ticketmaster',
                  eventbrite: 'Eventbrite',
                  webtenerife: 'WebTenerife',
                  santacruz: 'Agenda Santa Cruz',
                  icdcultural: 'ICD Cultural',
                  'cabildo-tenerife': 'Cabildo de Tenerife',
                  lucha_canaria: 'Fed. Lucha Canaria',
                }[selectedEvent.source] || selectedEvent.source
              }
            </p>
          )}

          <div className="mt-6 p-3 bg-gray-50 rounded-xl border border-gray-100" data-testid="event-disclaimer">
            <p className="text-[11px] text-gray-400 text-center leading-relaxed">
              {language === 'es' 
                ? 'Te recomendamos corroborar siempre la informacion directamente con el organizador. No nos hacemos responsables de cambios en los programas, cancelaciones o posibles errores.'
                : language === 'de'
                ? 'Wir empfehlen, die Informationen immer direkt beim Veranstalter zu überprüfen. Wir übernehmen keine Verantwortung für Programmänderungen, Absagen oder mögliche Fehler.'
                : 'We recommend always verifying the information directly with the organizer. We are not responsible for program changes, cancellations or possible errors.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Category filter view
  const filteredEvents = selectedCategory 
    ? events.filter(e => e.category === selectedCategory)
    : events;


  const titles = {
    es: { title: 'Eventos', subtitle: 'Conciertos, festivales y más en Tenerife', all: 'Todos' },
    en: { title: 'Events', subtitle: 'Concerts, festivals and more in Tenerife', all: 'All' },
    de: { title: 'Events', subtitle: 'Konzerte, Festivals und mehr auf Teneriffa', all: 'Alle' },
  };
  const t = titles[language] || titles.es;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#38B2AC]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="events-page">
      <SEO
        title={language === 'en' ? 'Events in Tenerife' : language === 'de' ? 'Veranstaltungen auf Teneriffa' : 'Eventos en Tenerife'}
        description={language === 'en' ? 'Cultural events, concerts, festivals, Lucha Canaria and more in Tenerife. Updated daily.' : language === 'de' ? 'Kulturveranstaltungen, Konzerte, Festivals, Lucha Canaria und mehr auf Teneriffa.' : 'Agenda cultural de Tenerife: conciertos, festivales, Lucha Canaria y mas. Actualizado a diario.'}
        path="/events"
        lang={language}
      />
      {/* Header */}
      <div className="px-5 pt-6 pb-4">
        <h1 className="text-2xl font-bold text-[#1D1D1F]">{t.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
      </div>

      {/* Category filters */}
      <div className="px-4 pb-4">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !selectedCategory 
                ? 'bg-[#38B2AC] text-white' 
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
            data-testid="filter-all"
          >
            {t.all}
          </button>
          {categories.map(cat => {
            const Icon = CATEGORY_ICONS[cat.slug] || CalendarDays;
            return (
              <button
                key={cat.slug}
                onClick={() => setSelectedCategory(cat.slug)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.slug 
                    ? 'bg-[#38B2AC] text-white' 
                    : 'bg-white text-gray-600 border border-gray-200'
                }`}
                data-testid={`filter-${cat.slug}`}
              >
                <Icon size={14} />
                {cat[`name_${language}`] || cat.name_es}
              </button>
            );
          })}
        </div>
      </div>

      {/* Events list */}
      <div className="px-4 space-y-6">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <CalendarDays size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">
              {language === 'es' ? 'No hay eventos proximos' : language === 'de' ? 'Keine kommenden Events' : 'No upcoming events'}
            </p>
          </div>
        ) : (
          <EventsList events={filteredEvents} categories={categories} language={language} onSelect={setSelectedEvent} />
        )}
      </div>
    </div>
  );
};

export default EventsPage;
